"use client";

import React from "react";
import type { BaseEntity } from "../types";
import type { SimpleTableSetupResult } from "../SimpleTableSetup";

/**
 * Pushes the table's quick-filter state into `FilterContext`.
 *
 * Quick filters are held in table-local state, but the server query reads
 * `FilterContext` — so without this the chips would narrow the client-side view
 * while GraphQL kept returning the unfiltered page. Extracted from `SimpleTable`
 * because it is a distinct responsibility (filter transport) from composing the
 * table's context and providers, and the composition module is at its size cap.
 *
 * Only non-empty values are forwarded: clearing a chip is handled by
 * `FilterContext`'s own reset path, not by writing an empty value here.
 */
export function useQuickFilterSync<TData extends BaseEntity>(
  setup: SimpleTableSetupResult<TData>
): void {
  const { quickFilterState, filterContext } = setup;

  React.useEffect(() => {
    if (!quickFilterState || Object.keys(quickFilterState).length === 0) return;
    if (!filterContext) return;

    Object.entries(quickFilterState).forEach(([filterId, value]) => {
      if (value !== undefined && value !== null && value !== "") {
        filterContext.actions.setSimpleFilter(filterId, value);
      }
    });
  }, [quickFilterState, filterContext]);
}
